/**
 * Centralized localStorage service
 * Provides type-safe, error-handled access to localStorage
 */
class StorageService {
  constructor() {
    this.prefix = 'coinzone_';
    this.removeLegacyToken();
  }

  /**
   * Older versions kept the admin token here, where any script on the page can read it.
   * The session is now an HttpOnly cookie, so remove whatever an earlier login left behind.
   */
  removeLegacyToken() {
    try {
      localStorage.removeItem(`${this.prefix}adminToken`);
      localStorage.removeItem('adminToken');
    } catch (error) {
      // storage may be unavailable; nothing to clean up then
    }
  }

  /**
   * Set an item in localStorage
   * @param {string} key - Storage key
   * @param {*} value - Value to store (will be JSON stringified)
   * @returns {boolean} Success status
   */
  setItem(key, value) {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(`${this.prefix}${key}`, serializedValue);
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }

  /**
   * Get an item from localStorage
   * @param {string} key - Storage key
   * @param {*} defaultValue - Default value if key doesn't exist
   * @returns {*} Parsed value or default
   */
  getItem(key, defaultValue = null) {
    try {
      // First try without prefix for backward compatibility
      let item = localStorage.getItem(`${this.prefix}${key}`);
      if (item === null) {
        // Try without prefix for backward compatibility
        item = localStorage.getItem(key);
      }
      if (item === null) return defaultValue;
      
      // Try to parse as JSON, if it fails, return as string (backward compatibility)
      try {
        return JSON.parse(item);
      } catch (parseError) {
        // If JSON.parse fails, it might be a plain string (backward compatibility)
        return item;
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Remove an item from localStorage
   * @param {string} key - Storage key
   * @param {boolean} removeLegacy - Also remove legacy key without prefix
   * @returns {boolean} Success status
   */
  removeItem(key, removeLegacy = true) {
    try {
      localStorage.removeItem(`${this.prefix}${key}`);
      // Also remove legacy key without prefix for backward compatibility
      if (removeLegacy) {
        localStorage.removeItem(key);
      }
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }

  /**
   * Clear all prefixed items from localStorage
   * Also clears legacy keys for backward compatibility
   * @returns {boolean} Success status
   */
  clear() {
    try {
      const keys = Object.keys(localStorage);
      const keysToRemove = [
        'adminToken',
        'adminName',
        'adminEmail',
        'userName',
        'device_id'
      ];
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix) || keysToRemove.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Clear all localStorage (use with caution)
   * @returns {boolean} Success status
   */
  clearAll() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing all localStorage:', error);
      return false;
    }
  }

  // Specific methods for admin data
  // These methods handle both prefixed and legacy keys for backward compatibility
  setAdminName(name) {
    this.setItem('adminName', name);
    try {
      localStorage.setItem('adminName', name); // Legacy key
    } catch (e) {
      // Ignore errors for legacy key
    }
    return true;
  }

  getAdminName() {
    return this.getItem('adminName');
  }

  setAdminEmail(email) {
    this.setItem('adminEmail', email);
    try {
      localStorage.setItem('adminEmail', email); // Legacy key
    } catch (e) {
      // Ignore errors for legacy key
    }
    return true;
  }

  getAdminEmail() {
    return this.getItem('adminEmail');
  }

  setDeviceId(deviceId) {
    this.setItem('device_id', deviceId);
    try {
      localStorage.setItem('device_id', deviceId); // Legacy key
    } catch (e) {
      // Ignore errors for legacy key
    }
    return true;
  }

  getDeviceId() {
    return this.getItem('device_id');
  }

  setUserName(userName) {
    this.setItem('userName', userName);
    try {
      localStorage.setItem('userName', userName); // Legacy key
    } catch (e) {
      // Ignore errors for legacy key
    }
    return true;
  }

  getUserName() {
    return this.getItem('userName');
  }
}

export const storageService = new StorageService();
export default storageService;

